import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { Trans } from 'react-i18next';
import { Glossary, type GlossaryTerm } from '../glossary/Glossary';
import { MainLayout } from '../MainLayout';
import { PageHeading } from '../layout/PageHeading';
import { useTranslation } from '@/hooks/use-translation';

/**
 * The order the abbreviations appear in the index at the top of the page:
 * alphabetical, which is how a reader looks one up.
 */
const INDEX_ORDER = ['BW', 'BWS', 'D', 'F', 'FW', 'HW', 'KF', 'KR', 'R', 'SBW', 'SS', 'SW', 'TBW', 'U', 'WBW', 'WW'];
export const HelpPage = () => {
  const $ = _c(229);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] !== t) {
    t0 = t("The minimum weight, to the nearest gram, placed on the measuring point that makes the key go down while keeping a slow, controlled hammer motion.");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("Down Weight");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      abbreviation: "D",
      description: t0,
      name: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t) {
    t3 = t("The maximum weight, to the nearest gram, placed on the measuring point that the key can still lift while keeping a slow, controlled hammer motion.");
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t) {
    t4 = t("Up Weight");
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = {
      abbreviation: "U",
      description: t3,
      name: t4
    };
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t) {
    t6 = t("The static weight, to the nearest tenth of a gram, that the level key exerts at the measuring point, tipped on its balance pin.");
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t) {
    t7 = t("Front Weight");
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = {
      abbreviation: "FW",
      description: t6,
      name: t7
    };
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t) {
    t9 = t("The weight, to the nearest tenth of a gram, of the shank and hammer, pivoting without friction at the hammer centre, shank horizontal, measured at the strike line radius.");
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t) {
    t10 = t("Strike Weight");
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t9) {
    t11 = {
      abbreviation: "SW",
      description: t9,
      name: t10
    };
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11 || $[29] !== t2 || $[30] !== t5 || $[31] !== t8) {
    t12 = [t2, t5, t8, t11];
    $[28] = t11;
    $[29] = t2;
    $[30] = t5;
    $[31] = t8;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const measuredTerms = t12;
  let t13;
  if ($[33] !== t) {
    t13 = t("The weight placed at the measuring point that brings the key into balance.");
    $[33] = t;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t) {
    t14 = t("Balance Weight");
    $[35] = t;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t13 || $[38] !== t14) {
    t15 = {
      abbreviation: "BW",
      description: t13,
      formula: "BW = (D + U) / 2",
      name: t14
    };
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t) {
    t16 = t("The minimum weight added to the balance weight that makes the key go down, or taken away to make it rise, while keeping a slow, controlled hammer motion.");
    $[40] = t;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t) {
    t17 = t("Friction Weight");
    $[42] = t;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t16 || $[45] !== t17) {
    t18 = {
      abbreviation: "F",
      description: t16,
      formula: "F = (D \u2212 U) / 2",
      name: t17
    };
    $[44] = t16;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t) {
    t19 = t("The weight to place at the measuring point to balance 1 g of strike weight, through the shank, the wippen and the key.");
    $[47] = t;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t) {
    t20 = t("Strike Weight Ratio");
    $[49] = t;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== t19 || $[52] !== t20) {
    t21 = {
      abbreviation: "R",
      description: t19,
      formula: "R = (FW + BW \u2212 WBW) / SW",
      name: t20
    };
    $[51] = t19;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t) {
    t22 = t("The upward static force at the measuring point coming from the weight of the wippen, carried through the key.");
    $[54] = t;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== t) {
    t23 = t("WBW = KR \xD7 wippen radial weight");
    $[56] = t;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t) {
    t24 = t("Wippen Balance Weight");
    $[58] = t;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t22 || $[61] !== t23 || $[62] !== t24) {
    t25 = {
      abbreviation: "WBW",
      description: t22,
      formula: t23,
      name: t24
    };
    $[60] = t22;
    $[61] = t23;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== t) {
    t26 = t("The difference between the balance weight with the support spring disengaged and with it engaged.");
    $[64] = t;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== t) {
    t27 = t("BWS = D without spring \u2212 D with spring");
    $[66] = t;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== t) {
    t28 = t("Support Spring Balance Weight");
    $[68] = t;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== t26 || $[71] !== t27 || $[72] !== t28) {
    t29 = {
      abbreviation: "BWS",
      description: t26,
      formula: t27,
      name: t28
    };
    $[70] = t26;
    $[71] = t27;
    $[72] = t28;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] !== t15 || $[75] !== t18 || $[76] !== t21 || $[77] !== t25 || $[78] !== t29) {
    t30 = [t15, t18, t21, t25, t29];
    $[74] = t15;
    $[75] = t18;
    $[76] = t21;
    $[77] = t25;
    $[78] = t29;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  const computedTerms = t30;
  let t31;
  if ($[80] !== t) {
    t31 = t("The weight at the measuring point that balances 1 g placed at the capstan contact point: the leverage of the key alone.");
    $[80] = t;
    $[81] = t31;
  } else {
    t31 = $[81];
  }
  let t32;
  if ($[82] !== t) {
    t32 = t("Key Weight Ratio");
    $[82] = t;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  let t33;
  if ($[84] !== t31 || $[85] !== t32) {
    t33 = {
      abbreviation: "KR",
      description: t31,
      name: t32
    };
    $[84] = t31;
    $[85] = t32;
    $[86] = t33;
  } else {
    t33 = $[86];
  }
  let t34;
  if ($[87] !== t) {
    t34 = t("The weight, to the nearest tenth of a gram, of the level wippen, pivoting without friction at its centre, measured at the capstan contact point.");
    $[87] = t;
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  let t35;
  if ($[89] !== t) {
    t35 = t("Wippen Weight");
    $[89] = t;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] !== t34 || $[92] !== t35) {
    t36 = {
      abbreviation: "WW",
      description: t34,
      name: t35
    };
    $[91] = t34;
    $[92] = t35;
    $[93] = t36;
  } else {
    t36 = $[93];
  }
  let t37;
  if ($[94] !== t) {
    t37 = t("The weight of the hammer with the shank removed.");
    $[94] = t;
    $[95] = t37;
  } else {
    t37 = $[95];
  }
  let t38;
  if ($[96] !== t) {
    t38 = t("Hammer Weight");
    $[96] = t;
    $[97] = t38;
  } else {
    t38 = $[97];
  }
  let t39;
  if ($[98] !== t37 || $[99] !== t38) {
    t39 = {
      abbreviation: "HW",
      description: t37,
      name: t38
    };
    $[98] = t37;
    $[99] = t38;
    $[100] = t39;
  } else {
    t39 = $[100];
  }
  let t40;
  if ($[101] !== t) {
    t40 = t("The weight, to the nearest tenth of a gram, of the shank alone, pivoting without friction at the hammer centre, measured at the strike line radius.");
    $[101] = t;
    $[102] = t40;
  } else {
    t40 = $[102];
  }
  let t41;
  if ($[103] !== t) {
    t41 = t("Shank Strike Weight");
    $[103] = t;
    $[104] = t41;
  } else {
    t41 = $[104];
  }
  let t42;
  if ($[105] !== t40 || $[106] !== t41) {
    t42 = {
      abbreviation: "SS",
      description: t40,
      name: t41
    };
    $[105] = t40;
    $[106] = t41;
    $[107] = t42;
  } else {
    t42 = $[107];
  }
  let t43;
  if ($[108] !== t) {
    t43 = t("The upward static force at the measuring point coming from the hammer and its shank, carried through the shank, the wippen and the key.");
    $[108] = t;
    $[109] = t43;
  } else {
    t43 = $[109];
  }
  let t44;
  if ($[110] !== t) {
    t44 = t("Strike Balance Weight");
    $[110] = t;
    $[111] = t44;
  } else {
    t44 = $[111];
  }
  let t45;
  if ($[112] !== t43 || $[113] !== t44) {
    t45 = {
      abbreviation: "SBW",
      description: t43,
      name: t44
    };
    $[112] = t43;
    $[113] = t44;
    $[114] = t45;
  } else {
    t45 = $[114];
  }
  let t46;
  if ($[115] !== t) {
    t46 = t("The combined force of the wippen and the hammer at the measuring point: WBW and SBW together.");
    $[115] = t;
    $[116] = t46;
  } else {
    t46 = $[116];
  }
  let t47;
  if ($[117] !== t) {
    t47 = t("Top Action Balance Weight");
    $[117] = t;
    $[118] = t47;
  } else {
    t47 = $[118];
  }
  let t48;
  if ($[119] !== t46 || $[120] !== t47) {
    t48 = {
      abbreviation: "TBW",
      description: t46,
      name: t47
    };
    $[119] = t46;
    $[120] = t47;
    $[121] = t48;
  } else {
    t48 = $[121];
  }
  let t49;
  if ($[122] !== t) {
    t49 = t("The share of the friction belonging to the key itself, measured on its frame with the action removed and the front weight cancelled by a temporary weight.");
    $[122] = t;
    $[123] = t49;
  } else {
    t49 = $[123];
  }
  let t50;
  if ($[124] !== t) {
    t50 = t("Key Friction Weight");
    $[124] = t;
    $[125] = t50;
  } else {
    t50 = $[125];
  }
  let t51;
  if ($[126] !== t49 || $[127] !== t50) {
    t51 = {
      abbreviation: "KF",
      description: t49,
      name: t50
    };
    $[126] = t49;
    $[127] = t50;
    $[128] = t51;
  } else {
    t51 = $[128];
  }
  let t52;
  if ($[129] !== t33 || $[130] !== t36 || $[131] !== t39 || $[132] !== t42 || $[133] !== t45 || $[134] !== t48 || $[135] !== t51) {
    t52 = [t33, t36, t39, t42, t45, t48, t51];
    $[129] = t33;
    $[130] = t36;
    $[131] = t39;
    $[132] = t42;
    $[133] = t45;
    $[134] = t48;
    $[135] = t51;
    $[136] = t52;
  } else {
    t52 = $[136];
  }
  const otherTerms = t52;
  let t53;
  if ($[137] !== t) {
    t53 = t("Front weight for a target balance weight");
    $[137] = t;
    $[138] = t53;
  } else {
    t53 = $[138];
  }
  let t54;
  if ($[139] !== t53) {
    t54 = {
      formula: "FW = WBW + R \xD7 SW \u2212 BW \u2212 BWS",
      label: t53
    };
    $[139] = t53;
    $[140] = t54;
  } else {
    t54 = $[140];
  }
  let t55;
  if ($[141] !== t) {
    t55 = t("Strike weight for a target balance weight");
    $[141] = t;
    $[142] = t55;
  } else {
    t55 = $[142];
  }
  let t56;
  if ($[143] !== t55) {
    t56 = {
      formula: "SW = (BW + FW + BWS \u2212 WBW) / R",
      label: t55
    };
    $[143] = t55;
    $[144] = t56;
  } else {
    t56 = $[144];
  }
  let t57;
  if ($[145] !== t) {
    t57 = t("Resulting balance weight");
    $[145] = t;
    $[146] = t57;
  } else {
    t57 = $[146];
  }
  let t58;
  if ($[147] !== t57) {
    t58 = {
      formula: "BW = SW \xD7 R + WBW \u2212 FW \u2212 BWS",
      label: t57
    };
    $[147] = t57;
    $[148] = t58;
  } else {
    t58 = $[148];
  }
  let t59;
  if ($[149] !== t54 || $[150] !== t56 || $[151] !== t58) {
    t59 = [t54, t56, t58];
    $[149] = t54;
    $[150] = t56;
    $[151] = t58;
    $[152] = t59;
  } else {
    t59 = $[152];
  }
  const designFormulas = t59;
  let t60;
  if ($[153] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <em className="ptl-accent">metrology</em>;
    $[153] = t60;
  } else {
    t60 = $[153];
  }
  let t61;
  let t62;
  if ($[154] !== t) {
    t61 = <Trans t={t} i18nKey="Touch weight <1>metrology</1> cheat sheet">Touch weight {t60} cheat sheet</Trans>;
    t62 = t("The quantities Piano Touch Lab uses, after David C. Stanwood\u2019s \u201CNew Touch Weight Metrology\u201D.");
    $[154] = t;
    $[155] = t61;
    $[156] = t62;
  } else {
    t61 = $[155];
    t62 = $[156];
  }
  let t63;
  if ($[157] !== t61 || $[158] !== t62) {
    t63 = <PageHeading title={t61} lede={t62} />;
    $[157] = t61;
    $[158] = t62;
    $[159] = t63;
  } else {
    t63 = $[159];
  }
  let t64;
  if ($[160] !== t) {
    t64 = t("Index of abbreviations");
    $[160] = t;
    $[161] = t64;
  } else {
    t64 = $[161];
  }
  let t65;
  if ($[162] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = INDEX_ORDER.map(_temp);
    $[162] = t65;
  } else {
    t65 = $[162];
  }
  let t66;
  if ($[163] !== t64) {
    t66 = <nav aria-label={t64} className="-mt-6 flex flex-wrap gap-1.5">{t65}</nav>;
    $[163] = t64;
    $[164] = t66;
  } else {
    t66 = $[164];
  }
  let t67;
  if ($[165] !== t) {
    t67 = t("What you measure");
    $[165] = t;
    $[166] = t67;
  } else {
    t67 = $[166];
  }
  let t68;
  if ($[167] !== t67) {
    t68 = <h2 className="m-0 font-serif text-[28px] font-medium">{t67}</h2>;
    $[167] = t67;
    $[168] = t68;
  } else {
    t68 = $[168];
  }
  let t69;
  if ($[169] !== measuredTerms) {
    t69 = <Glossary terms={measuredTerms} />;
    $[169] = measuredTerms;
    $[170] = t69;
  } else {
    t69 = $[170];
  }
  let t70;
  if ($[171] !== t68 || $[172] !== t69) {
    t70 = <section className="flex flex-col gap-3.5">{t68}{t69}</section>;
    $[171] = t68;
    $[172] = t69;
    $[173] = t70;
  } else {
    t70 = $[173];
  }
  let t71;
  if ($[174] !== t) {
    t71 = t("What the tool computes");
    $[174] = t;
    $[175] = t71;
  } else {
    t71 = $[175];
  }
  let t72;
  if ($[176] !== t71) {
    t72 = <h2 className="m-0 font-serif text-[28px] font-medium">{t71}</h2>;
    $[176] = t71;
    $[177] = t72;
  } else {
    t72 = $[177];
  }
  let t73;
  if ($[178] !== computedTerms) {
    t73 = <Glossary terms={computedTerms} />;
    $[178] = computedTerms;
    $[179] = t73;
  } else {
    t73 = $[179];
  }
  let t74;
  if ($[180] !== t72 || $[181] !== t73) {
    t74 = <section className="flex flex-col gap-3.5">{t72}{t73}</section>;
    $[180] = t72;
    $[181] = t73;
    $[182] = t74;
  } else {
    t74 = $[182];
  }
  let t75;
  if ($[183] !== t) {
    t75 = t("Other terms");
    $[183] = t;
    $[184] = t75;
  } else {
    t75 = $[184];
  }
  let t76;
  if ($[185] !== t75) {
    t76 = <h2 className="m-0 font-serif text-[28px] font-medium">{t75}</h2>;
    $[185] = t75;
    $[186] = t76;
  } else {
    t76 = $[186];
  }
  let t77;
  if ($[187] !== otherTerms) {
    t77 = <Glossary terms={otherTerms} />;
    $[187] = otherTerms;
    $[188] = t77;
  } else {
    t77 = $[188];
  }
  let t78;
  if ($[189] !== t76 || $[190] !== t77) {
    t78 = <section className="flex flex-col gap-3.5">{t76}{t77}</section>;
    $[189] = t76;
    $[190] = t77;
    $[191] = t78;
  } else {
    t78 = $[191];
  }
  let t79;
  if ($[192] !== t) {
    t79 = t("Design formulas");
    $[192] = t;
    $[193] = t79;
  } else {
    t79 = $[193];
  }
  let t80;
  if ($[194] !== t79) {
    t80 = <h2 className="m-0 mb-3 font-serif text-[26px] font-medium">{t79}</h2>;
    $[194] = t79;
    $[195] = t80;
  } else {
    t80 = $[195];
  }
  let t81;
  if ($[196] !== designFormulas) {
    t81 = designFormulas.map(_temp2);
    $[196] = designFormulas;
    $[197] = t81;
  } else {
    t81 = $[197];
  }
  let t82;
  if ($[198] !== t80 || $[199] !== t81) {
    t82 = <section className="flex-[3_1_420px] rounded-md border border-ptl-rule-strong bg-card px-6 pt-[22px] pb-3">{t80}{t81}</section>;
    $[198] = t80;
    $[199] = t81;
    $[200] = t82;
  } else {
    t82 = $[200];
  }
  let t83;
  if ($[201] !== t) {
    t83 = t("Going further");
    $[201] = t;
    $[202] = t83;
  } else {
    t83 = $[202];
  }
  let t84;
  if ($[203] !== t83) {
    t84 = <h2 className="m-0 font-serif text-[26px] font-medium">{t83}</h2>;
    $[203] = t83;
    $[204] = t84;
  } else {
    t84 = $[204];
  }
  let t85;
  if ($[205] === Symbol.for("react.memo_cache_sentinel")) {
    t85 = <span className="font-semibold">David C. Stanwood — Piano Technicians Resource Page</span>;
    $[205] = t85;
  } else {
    t85 = $[205];
  }
  let t86;
  if ($[206] !== t) {
    t86 = t("Technical documents on analysing and improving touch weight.");
    $[206] = t;
    $[207] = t86;
  } else {
    t86 = $[207];
  }
  let t87;
  if ($[208] !== t86) {
    t87 = <a href="https://www.stanwoodpiano.com/touchweight.htm" target="_blank" rel="noreferrer" className="flex flex-col gap-1 rounded-md border border-border bg-card px-4 py-3.5 text-foreground transition-colors hover:bg-accent">{t85}<span className="text-[13.5px] text-muted-foreground">{t86}</span></a>;
    $[208] = t86;
    $[209] = t87;
  } else {
    t87 = $[209];
  }
  let t88;
  if ($[210] === Symbol.for("react.memo_cache_sentinel")) {
    t88 = <span className="font-serif text-lg italic">Pianos Inside Out</span>;
    $[210] = t88;
  } else {
    t88 = $[210];
  }
  let t89;
  if ($[211] !== t) {
    t89 = t("Mario Igrec \u2014 a reference guide to piano tuning, repair and rebuilding.");
    $[211] = t;
    $[212] = t89;
  } else {
    t89 = $[212];
  }
  let t90;
  if ($[213] !== t89) {
    t90 = <div className="flex flex-col gap-1 rounded-md border border-border bg-card px-4 py-3.5">{t88}<span className="text-[13.5px] text-muted-foreground">{t89}</span></div>;
    $[213] = t89;
    $[214] = t90;
  } else {
    t90 = $[214];
  }
  let t91;
  if ($[215] !== t84 || $[216] !== t87 || $[217] !== t90) {
    t91 = <section className="flex flex-[2_1_320px] flex-col gap-3">{t84}{t87}{t90}</section>;
    $[215] = t84;
    $[216] = t87;
    $[217] = t90;
    $[218] = t91;
  } else {
    t91 = $[218];
  }
  let t92;
  if ($[219] !== t82 || $[220] !== t91) {
    t92 = <div className="flex flex-wrap items-start gap-6">{t82}{t91}</div>;
    $[219] = t82;
    $[220] = t91;
    $[221] = t92;
  } else {
    t92 = $[221];
  }
  let t93;
  if ($[222] !== t63 || $[223] !== t66 || $[224] !== t70 || $[225] !== t74 || $[226] !== t78 || $[227] !== t92) {
    t93 = <MainLayout shouldDisplayDemoBanner={false} contentClassName="gap-10">{t63}{t66}{t70}{t74}{t78}{t92}</MainLayout>;
    $[222] = t63;
    $[223] = t66;
    $[224] = t70;
    $[225] = t74;
    $[226] = t78;
    $[227] = t92;
    $[228] = t93;
  } else {
    t93 = $[228];
  }
  return t93;
};
function _temp(abbreviation) {
  return <a key={abbreviation} href={`#term-${abbreviation}`} className="inline-flex h-9 min-w-11 items-center justify-center rounded-md border border-input px-2 font-mono text-[13px] text-foreground transition-colors hover:bg-accent">{abbreviation}</a>;
}
function _temp2(entry) {
  return <div key={entry.formula} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] items-baseline gap-x-5 gap-y-1.5 border-t border-border py-3"><span className="text-sm text-muted-foreground">{entry.label}</span><span className="font-mono text-sm">{entry.formula}</span></div>;
}